import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAllModulePermissions } from "@/hooks/useModuleAccess";
import { useUserRole } from "@/hooks/useUserRole";
import {
  LayoutDashboard,
  Users,
  FlaskConical,
  ClipboardCheck,
  FileText,
  Settings,
  Wrench,
  Truck,
  HardHat,
  FileSpreadsheet,
  GraduationCap,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ShieldCheck,
  Bot,
  MessageSquare,
  Package,
  Layers,
  Plus,
  Factory,
  Warehouse,
  ShoppingCart,
  ShoppingBag,
  Boxes,
  ClipboardList,
  Grid3X3,
  BarChart3,
  TrendingUp,
  FileCheck2,
  Contact,
  Tag,
  CreditCard,
  Clock,
  Inbox as InboxIcon,
  Settings2,
  UserCog,
  BookOpen,
  ScrollText,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useSidebarConversations } from "@/hooks/useSidebarConversations";
import logo from "@/assets/logo.png";
import logoWhite from "@/assets/Superior Formulations Logo 2026 WHITE.png";
import logoIcon from "@/assets/SF 2026 icon.png";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
  isMobile?: boolean;
}

interface SubItem {
  name: string;
  icon: React.ElementType;
  path: string;
  /** When set, this child is "active" if the current ?tab= value is in this list */
  tabGroup?: string[];
}

interface MenuItem {
  name: string;
  icon: React.ElementType;
  path: string;
  module: string | null;
  children?: SubItem[];
  adminOnly?: boolean;
}

interface SidebarSection {
  label: string;
  items: MenuItem[];
}

const sidebarSections: SidebarSection[] = [
  {
    label: "OPERATIONS",
    items: [
      { name: "Dashboard", icon: LayoutDashboard, path: "/", module: "dashboard" },
      { name: "Tasks", icon: ClipboardList, path: "/tasks", module: "dashboard" },
      {
        name: "Warehouse", icon: Warehouse, path: "/warehouse", module: "dashboard",
        children: [
          { name: "Inventory", icon: Layers, path: "/warehouse/inventory" },
          { name: "Inbound", icon: Package, path: "/warehouse/inbound" },
          { name: "Outbound", icon: Truck, path: "/warehouse/outbound" },
          { name: "JRB", icon: Grid3X3, path: "/warehouse/jrb" },
          { name: "Analytics & Ops", icon: TrendingUp, path: "/warehouse/analytics" },
        ],
      },
      {
        name: "Manufacturing", icon: Factory, path: "/manufacturing", module: "manufacturing",
        children: [
          { name: "Labels", icon: Tag, path: "/manufacturing/labels" },
        ],
      },
      { name: "Planning", icon: CalendarRange, path: "/planning", module: "dashboard" },
      { name: "Purchasing", icon: ShoppingCart, path: "/purchasing", module: "dashboard" },
      { name: "Consumables", icon: ShoppingBag, path: "/consumables", module: "consumables" },
    ],
  },
  {
    label: "QUALITY & COMPLIANCE",
    items: [
      {
        name: "Quality", icon: ClipboardCheck, path: "/quality", module: "quality",
        children: [
          { name: "In-Process QC", icon: FileCheck2, path: "/quality/in-process-qc" },
          { name: "Lot Trace", icon: Search, path: "/quality/lot-trace" },
        ],
      },
      // Label proofing had no sidebar entry and was reachable only by URL or
      // from NPD (plan P-7a).
      {
        name: "Label Proofing", icon: Tag, path: "/proofing", module: "label_proofing",
        children: [
          { name: "Compliance", icon: ShieldCheck, path: "/proofing/compliance" },
          { name: "Projects", icon: FileCheck2, path: "/proofing" },
          { name: "Regulatory Knowledge Base", icon: BookOpen, path: "/proofing/regulatory-sources" },
        ],
      },
      { name: "Documents", icon: FileText, path: "/documents", module: "documents" },
      { name: "Forms", icon: FileSpreadsheet, path: "/forms", module: "forms" },
    ],
  },
  {
    label: "MASTER DATA",
    items: [
      { name: "Stock Items", icon: Boxes, path: "/stock-items", module: "stock_items" },
      { name: "Products", icon: Package, path: "/products", module: "stock_items" },
      { name: "Suppliers", icon: Truck, path: "/suppliers", module: "suppliers" },
    ],
  },
  {
    label: "CUSTOMER OPERATIONS",
    items: [
      { name: "CRM", icon: Users, path: "/crm", module: "crm" },
      { name: "Payments", icon: CreditCard, path: "/payments", module: "crm" },
      {
        name: "NPD", icon: FlaskConical, path: "/npd", module: "npd",
        children: [
          { name: "RFQ List", icon: ShoppingCart, path: "/npd/rfq" },
          { name: "Dispatch", icon: Truck, path: "/npd/dispatch" },
        ],
      },
    ],
  },
  {
    // Strictly-HR section, isolated from Workforce & Assets. Timesheets stays
    // here (its own `timesheets` gate); the rest are gated on the `hr` module
    // so only admins / hr_manager see them. The section header itself is hidden
    // for users with none of these permissions (see nav render below).
    label: "HUMAN RESOURCES",
    items: [
      {
        name: "HR / Timesheets", icon: Clock, path: "/hr/timesheets", module: "timesheets",
        children: [
          { name: "Timesheets Inbox", icon: InboxIcon, path: "/hr/timesheets" },
          { name: "Break Rules", icon: Settings2, path: "/hr/timesheets/rules" },
        ],
      },
      // Gated on the `hr` module — only admins / hr_manager see these entries.
      { name: "Staff Profiles", icon: UserCog, path: "/hr/staff", module: "hr" },
      { name: "HR Assistant", icon: Bot, path: "/hr/assistant", module: "hr" },
      { name: "HR Knowledge", icon: BookOpen, path: "/hr/knowledge", module: "hr" },
      { name: "HR Audit", icon: ScrollText, path: "/hr/audit", module: "hr" },
    ],
  },
  {
    label: "WORKFORCE & ASSETS",
    items: [
      { name: "Contacts", icon: Contact, path: "/contacts", module: "contacts" },
      { name: "Equipment", icon: Wrench, path: "/equipment", module: "equipment" },
      { name: "Training", icon: GraduationCap, path: "/training", module: "training" },
      { name: "Contractors", icon: HardHat, path: "/contractors", module: "contractors" },
    ],
  },
  {
    label: "ADMIN",
    items: [
      { name: "AI Assistant", icon: Bot, path: "/ai-chat", module: "dashboard" },
      { name: "Settings", icon: Settings, path: "/settings", module: "settings" },
      { name: "Admin", icon: ShieldCheck, path: "/admin", module: null, adminOnly: true },
    ],
  },
];

// Flat list for auto-expand logic on mount
const allMenuItems = sidebarSections.flatMap((s) => s.items);

/** Match a nav path (which may include query params) against the current location. */
function isPathActive(path: string, location: { pathname: string; search: string }): boolean {
  const [pathPart, queryPart] = path.split("?");
  if (pathPart === "/") {
    return location.pathname === "/" && !queryPart;
  }
  if (location.pathname !== pathPart) return false;
  if (!queryPart) return !location.search || location.search === "?";
  // Compare specific query param (e.g. tab=picking)
  const navParams = new URLSearchParams(queryPart);
  const currentParams = new URLSearchParams(location.search);
  for (const [key, val] of navParams.entries()) {
    if (currentParams.get(key) !== val) return false;
  }
  return true;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function Sidebar({ collapsed, onToggle, onNavigate, isMobile }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { permissions } = useAllModulePermissions();
  const { isAdmin, isTech } = useUserRole();
  const { conversations, totalUnread } = useSidebarConversations();
  /** Check if a child is active, using tabGroup or pathname matching */
  const isChildActive = (child: SubItem) => {
    if (child.tabGroup) {
      const currentTab = new URLSearchParams(location.search).get("tab");
      return child.tabGroup.includes(currentTab || "");
    }
    // For route-based children (e.g. /warehouse/inventory), match pathname
    // regardless of query params so ?tab=levels still highlights the parent
    const [pathPart] = child.path.split("?");
    if (pathPart !== "/" && location.pathname === pathPart) return true;
    return isPathActive(child.path, location);
  };

  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => {
    // Auto-expand parent if a child route is active on mount
    const expanded = new Set<string>();
    for (const item of allMenuItems) {
      if (item.children?.some(c => isChildActive(c))) {
        expanded.add(item.path);
      }
    }
    return expanded;
  });

  // Match paths that may include query params (e.g. /warehouse?tab=picking)
  const isActive = (path: string) => isPathActive(path, location);

  const isParentActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const canAccess = (module: string) => {
    const perm = permissions[module];
    return perm && perm !== "none";
  };

  // Mirrors NavItem's own visibility gate. Used to hide a whole section header
  // when none of its items are visible — important for the sensitive HUMAN
  // RESOURCES section so it isn't disclosed to users without hr/timesheets.
  const isItemVisible = (item: MenuItem) => {
    if (item.module && !canAccess(item.module)) return false;
    if (item.adminOnly && !isAdmin && !isTech) return false;
    return true;
  };

  const toggleExpand = (path: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const NavItem = ({ item }: { item: MenuItem }) => {
    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;
    const active = hasChildren
      ? isParentActive(item.path) && !item.children!.some(c => isChildActive(c))
      : isActive(item.path);
    const parentActive = isParentActive(item.path);
    const isExpanded = expandedItems.has(item.path);
    const hasAccess = item.module ? canAccess(item.module) : true;

    if (!hasAccess) return null;
    if (item.adminOnly && !isAdmin && !isTech) return null;

    const handleClick = () => {
      onNavigate?.();
    };

    // Collapsed sidebar — icon with floating card flyout
    if (collapsed && !isMobile) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <div>
              <Link
                to={item.path}
                onClick={handleClick}
                className={cn(
                  "flex items-center justify-center px-2 py-2 rounded-xl transition-all duration-150 group",
                  active
                    ? "bg-[#1B9EC9]/15 text-white shadow-sm"
                    : parentActive && hasChildren
                    ? "text-[#1B9EC9] hover:bg-white/[0.06]"
                    : "text-white/80 hover:bg-white/[0.06]",
                )}
              >
                <Icon className={cn(
                  "h-5 w-5 shrink-0 transition-transform duration-150",
                  !active && "group-hover:scale-110"
                )} />
              </Link>
            </div>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            sideOffset={8}
            className={cn(
              "font-medium",
              hasChildren && "p-2 min-w-[160px]"
            )}
          >
            {hasChildren ? (
              <div>
                <Link
                  to={item.path}
                  onClick={handleClick}
                  className="block text-sm font-semibold mb-1.5 hover:underline"
                >
                  {item.name}
                </Link>
                <div className="border-t border-border pt-1.5 space-y-0.5">
                  {item.children!.map(child => {
                    const childIsActive = isChildActive(child);
                    return (
                      <Link
                        key={child.path}
                        to={child.path}
                        onClick={handleClick}
                        className={cn(
                          "flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors",
                          childIsActive
                            ? "bg-accent font-medium text-accent-foreground"
                            : "hover:bg-accent/50 text-muted-foreground hover:text-foreground"
                        )}
                      >
                        <span className={cn(
                          "h-1.5 w-1.5 rounded-full shrink-0",
                          childIsActive ? "bg-primary" : "bg-muted-foreground/40"
                        )} />
                        {child.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ) : (
              <span>{item.name}</span>
            )}
          </TooltipContent>
        </Tooltip>
      );
    }

    // Expanded sidebar — full row with inline chevron
    return (
      <div>
        <div
          className={cn(
            "flex items-center rounded-xl transition-all duration-150",
            active && "bg-[#1B9EC9]/15 border-l-[3px] border-[#1B9EC9] text-white shadow-sm",
            !active && hasChildren && isExpanded && "bg-white/[0.06]",
            !active && !isExpanded && "hover:bg-white/[0.06]",
            active && hasChildren && isExpanded && "bg-[#1B9EC9]/15 border-l-[3px] border-[#1B9EC9] text-white shadow-sm",
          )}
        >
          <Link
            to={item.path}
            onClick={handleClick}
            className={cn(
              "flex items-center gap-3 px-3 py-2 flex-1 min-w-0 group",
              isMobile && "py-3",
              !active && (parentActive && hasChildren
                ? "text-[#1B9EC9]"
                : "text-white/80"),
            )}
          >
            <Icon className={cn(
              "h-5 w-5 shrink-0 transition-transform duration-150",
              !active && "group-hover:scale-110"
            )} />
            <span className="truncate font-medium text-sm">{item.name}</span>
          </Link>
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(item.path);
              }}
              className={cn(
                "p-1.5 mr-1.5 rounded-md transition-colors shrink-0",
                active
                  ? "text-white/70 hover:text-white hover:bg-white/10"
                  : "text-white/40 hover:text-white/80 hover:bg-white/[0.06]"
              )}
            >
              <ChevronDown className={cn(
                "h-3.5 w-3.5 transition-transform duration-200",
                isExpanded && "rotate-180"
              )} />
            </button>
          )}
        </div>

        {/* Sub-items with dot/line connectors */}
        {hasChildren && isExpanded && (
          <div className="relative ml-[22px] mt-0.5 mb-0.5">
            {/* Vertical connector line */}
            <div className="absolute left-0 top-0 bottom-2 w-px bg-sidebar-border" />
            <div className="space-y-px">
              {item.children!.map(child => {
                const childActive = isChildActive(child);
                return (
                  <Link
                    key={child.path}
                    to={child.path}
                    onClick={handleClick}
                    className={cn(
                      "relative flex items-center pl-5 pr-3 py-1.5 rounded-md transition-all duration-150 text-sm group",
                      childActive
                        ? "bg-white/[0.08] text-white font-medium"
                        : "text-white/60 hover:bg-white/[0.04] hover:text-white/90"
                    )}
                  >
                    {/* Horizontal connector + dot */}
                    <div className="absolute left-0 top-1/2 w-3.5 h-px bg-sidebar-border" />
                    <div className={cn(
                      "absolute left-[-2.5px] top-1/2 -translate-y-1/2 h-[5px] w-[5px] rounded-full border border-sidebar-border",
                      childActive
                        ? "bg-[#1B9EC9] border-[#1B9EC9]"
                        : "bg-[#0D1828]"
                    )} />
                    <span className="truncate">{child.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className={cn(
        "flex items-center justify-center h-16 px-4 border-b border-sidebar-border shrink-0",
        isMobile && "h-14 justify-start"
      )}>
        <Link to="/" onClick={onNavigate} className="flex items-center">
          {collapsed && !isMobile ? (
            <img src={logoIcon} alt="Superior Formulations" className="h-9 w-9 object-contain" />
          ) : (
            <img src={logoWhite} alt="Superior Formulations" className="h-9 w-auto object-contain" />
          )}
        </Link>
      </div>

      {/* Navigation - centered on desktop */}
      <ScrollArea className="flex-1 flex">
        <nav className={cn(
          "flex flex-col p-2 w-full",
          !isMobile && "my-auto"
        )}>
          {sidebarSections
            // Hide a section entirely when the user can see none of its items,
            // so empty section headers (e.g. HUMAN RESOURCES for non-HR users)
            // are never rendered.
            .filter((section) => section.items.some(isItemVisible))
            .map((section, idx) => (
              <div key={section.label} className={cn(idx > 0 && "mt-3")}>
                {/* Section header — hidden when collapsed */}
                {(!collapsed || isMobile) && (
                  <div className="px-3 pt-1 pb-1.5">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#4B6A9B]">
                      {section.label}
                    </span>
                  </div>
                )}
                {collapsed && !isMobile && idx > 0 && (
                  <div className="mx-2 my-1 border-t border-sidebar-border" />
                )}
                <div className="space-y-0.5">
                  {section.items.map((item) => (
                    <NavItem key={item.path} item={item} />
                  ))}
                </div>
              </div>
            ))}
        </nav>
      </ScrollArea>

      {/* Messages section */}
      <div className="shrink-0 border-t border-sidebar-border">
        {collapsed && !isMobile ? (
          /* Collapsed: avatar circles with tooltips */
          <div className="flex flex-col items-center gap-1.5 py-2">
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <button
                  onClick={() => { navigate("/messages?new=true"); onNavigate?.(); }}
                  className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>
                <span>New message</span>
              </TooltipContent>
            </Tooltip>
            {conversations.map((conv) => (
              <Tooltip key={conv.id} delayDuration={0}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => { navigate(`/messages?conv=${conv.id}`); onNavigate?.(); }}
                    className="relative"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-[10px] bg-sidebar-accent text-sidebar-foreground">
                        {getInitials(conv.recipientName || conv.recipientEmail)}
                      </AvatarFallback>
                    </Avatar>
                    {conv.isOnline && (
                      <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-green-500 border-2 border-sidebar" />
                    )}
                    {conv.unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-destructive text-[8px] text-white flex items-center justify-center font-bold">
                        {conv.unreadCount > 9 ? "9+" : conv.unreadCount}
                      </span>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8}>
                  <span>{conv.recipientName || conv.recipientEmail}</span>
                  {conv.unreadCount > 0 && (
                    <span className="ml-1.5 text-muted-foreground">({conv.unreadCount} unread)</span>
                  )}
                </TooltipContent>
              </Tooltip>
            ))}
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Link
                  to="/messages"
                  onClick={onNavigate}
                  className="flex items-center justify-center w-8 h-8 rounded-full text-sidebar-muted hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
                >
                  <MessageSquare className="h-4 w-4" />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>
                <span>View all messages</span>
              </TooltipContent>
            </Tooltip>
          </div>
        ) : (
          /* Expanded: section header + conversation rows + view all */
          <div className="p-2 space-y-1">
            <div className="flex items-center justify-between px-3 py-1">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-sidebar-muted flex items-center gap-2">
                Messages
                {totalUnread > 0 && (
                  <Badge variant="destructive" className="h-4 min-w-[16px] px-1 text-[9px] leading-none">
                    {totalUnread}
                  </Badge>
                )}
              </span>
              <button
                onClick={() => { navigate("/messages?new=true"); onNavigate?.(); }}
                className="p-1 rounded-md text-white/60 hover:text-white hover:bg-white/[0.06] transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
            {conversations.map((conv) => (
              <button
                key={conv.id}
                onClick={() => { navigate(`/messages?conv=${conv.id}`); onNavigate?.(); }}
                className={cn(
                  "flex items-center gap-2.5 w-full px-3 py-1.5 rounded-lg transition-colors text-left",
                  "text-sidebar-foreground hover:bg-sidebar-accent",
                  isMobile && "py-2.5"
                )}
              >
                <div className="relative shrink-0">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="text-[10px] bg-sidebar-accent text-sidebar-foreground">
                      {getInitials(conv.recipientName || conv.recipientEmail)}
                    </AvatarFallback>
                  </Avatar>
                  {conv.isOnline && (
                    <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-green-500 border-[1.5px] border-sidebar" />
                  )}
                </div>
                <span className="truncate text-sm font-medium flex-1 min-w-0">
                  {conv.recipientName || conv.recipientEmail}
                </span>
                {conv.unreadCount > 0 && (
                  <Badge variant="destructive" className="h-4 min-w-[16px] px-1 text-[9px] leading-none shrink-0">
                    {conv.unreadCount}
                  </Badge>
                )}
              </button>
            ))}
            <Link
              to="/messages"
              onClick={onNavigate}
              className="flex items-center gap-2 px-3 py-1 text-xs text-white/60 hover:text-white transition-colors"
            >
              <span>View all</span>
              <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
        )}
      </div>

      {/* Collapse toggle — desktop only */}
      {!isMobile && (
        <div className="shrink-0 p-3 border-t border-sidebar-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            className={cn(
              "w-full justify-start text-white/70 hover:text-white hover:bg-white/[0.06]",
              collapsed && "justify-center"
            )}
          >
            {collapsed ? (
              <ChevronRight className="h-5 w-5" />
            ) : (
              <>
                <ChevronLeft className="h-5 w-5 mr-2" />
                <span>Collapse</span>
              </>
            )}
          </Button>
        </div>
      )}
    </>
  );

  if (isMobile) {
    return (
      <div className="flex flex-col h-full bg-sidebar relative overflow-hidden">
        <div
          className="pointer-events-none absolute z-0"
          style={{
            width: "300px",
            height: "308px",
            top: "-1%",
            right: "-30%",
            background: "radial-gradient(ellipse at center, rgba(27,158,201,0.12) 0%, transparent 70%)",
          }}
        />
        <div
          className="pointer-events-none absolute z-0"
          style={{
            width: "320px",
            height: "330px",
            bottom: "-5%",
            left: "-50%",
            background: "radial-gradient(ellipse at center, rgba(61,191,224,0.09) 0%, transparent 70%)",
          }}
        />
        <div className="relative z-10 flex flex-col h-full">
          {sidebarContent}
        </div>
      </div>
    );
  }

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-40 h-screen bg-sidebar border-r border-sidebar-border transition-all duration-300 flex flex-col overflow-hidden",
        collapsed ? "w-16" : "w-64"
      )}
    >
      {/* Dual glow flares — behind menu items, bleed off edges */}
      <div
        className="pointer-events-none absolute z-0"
        style={{
          width: "300px",
          height: "308px",
          top: "-1%",
          right: "-30%",
          background: "radial-gradient(ellipse at center, rgba(27,158,201,0.12) 0%, transparent 70%)",
        }}
      />
      <div
        className="pointer-events-none absolute z-0"
        style={{
          width: "320px",
          height: "330px",
          bottom: "-5%",
          left: "-50%",
          background: "radial-gradient(ellipse at center, rgba(61,191,224,0.09) 0%, transparent 70%)",
        }}
      />
      <div className="relative z-10 flex flex-col h-full">
        {sidebarContent}
      </div>
    </aside>
  );
}
